import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Users, ChevronRight, ChevronLeft, Luggage, Check, Calendar, X, Maximize2 } from "lucide-react";


import { ROOF_LABEL, type VehicleFamily } from "@/data/vehicleFamilies";
import { loadTripDates } from "@/lib/tripDates";

import { useFamilyPricing, usePricingPayload, resolveRate } from "@/hooks/usePricing";
import ConfigureOptionsSheet from "./ConfigureOptionsSheet";
import SeatingDiagram from "./SeatingDiagram";
import { getDiagram } from "./ConfigureOptionsSheet";
import sprinterCargoOverride from "@/assets/fleet-card/sprinter-cargo.png";
import sprinter12DiagramOverride from "@/assets/fleet-card/sprinter-12-diagram.png";
import transit12DiagramOverride from "@/assets/fleet-card/transit-12-diagram.png";
import transitLowCargoOverride from "@/assets/fleet-card/transit-low-cargo-v2.png";
import transit15DiagramOverride from "@/assets/fleet-card/transit-15-diagram.png";
import transitHighCargoOverride from "@/assets/fleet-card/transit-high-cargo.png";
import expressCargoOverride from "@/assets/fleet-card/express-cargo.png";
import transitLowCargo2 from "@/assets/fleet-card/transit-low-cargo-2.png";
import transitMidCargo2 from "@/assets/fleet-card/transit-mid-cargo-2.png";
import sprinterDiag15High from "@/assets/sprinter-diagrams/sprinter-15-high.png";

// Fleet-card-only overrides (do not affect /configure page).
function getCardCargoOverride(slug: string): string | null {
  if (slug === "mercedes-sprinter" || slug === "mercedes-sprinter-older") return sprinterCargoOverride;
  if (slug === "ford-transit-low") return transitLowCargoOverride;
  if (slug === "ford-transit-high") return transitHighCargoOverride;
  if (slug === "chevy-express") return expressCargoOverride;
  return null;
}
function getCardCargoSecondary(slug: string): string | null {
  if (slug === "ford-transit-low") return transitLowCargo2;
  if (slug === "ford-transit") return transitMidCargo2;
  if (slug === "mercedes-sprinter" || slug === "mercedes-sprinter-older") return sprinterCargoOverride;
  return null;
}
function getCardDiagramOverride(slug: string, passengers: number): string | null {
  if (slug === "mercedes-sprinter" && passengers === 12) return sprinter12DiagramOverride;
  if ((slug === "ford-transit" || slug === "ford-transit-low") && passengers === 12) return transit12DiagramOverride;
  if ((slug === "ford-transit" || slug === "ford-transit-low") && passengers === 15) return transit15DiagramOverride;
  if (slug === "ford-transit-high" && passengers === 15) return sprinterDiag15High;
  return null;
}

interface Props {
  family: VehicleFamily;
  city?: string;
  accent?: string;
  cardBg?: string;
  textClass?: string;
  /** When set, replaces the default CTA row with a single "Select This Van" button. */
  onSelectFamily?: (family: VehicleFamily) => void;
}

export default function VehicleFamilyCard({
  family,
  city,
  accent = "#F97316",
  cardBg = "bg-white",
  textClass = "text-[#1a1a1a]",
  onSelectFamily,
}: Props) {
  const [open, setOpen] = useState(false);
  const minP = Math.min(...family.passengerOptions);
  const maxP = Math.max(...family.passengerOptions);

  // Hide the entire family card if the CRM has no live rate for the smallest
  // passenger config — the site renders exactly what the CRM exposes.
  const availability = useFamilyPricing(family, minP, family.tvOptions[0], city ?? null);
  if (availability.isLoading) {
    return (
      <div
        className={`${cardBg} rounded-3xl overflow-hidden shadow-2xl border animate-pulse h-full`}
        style={{ borderColor: `${accent}33` }}
      >
        <div className="aspect-[4/3] bg-muted" />
        <div className="p-6 space-y-3">
          <div className="h-6 bg-muted rounded w-2/3" />
          <div className="h-4 bg-muted rounded w-1/3" />
          <div className="h-10 bg-muted rounded" />
        </div>
      </div>
    );
  }
  if (availability.isUnavailable || !availability.rate || availability.rate.daily_rate == null) {
    return null;
  }

  return (
    <>
      <article
        className={`${cardBg} group relative rounded-3xl overflow-hidden shadow-2xl border transition duration-500 hover:-translate-y-1 flex flex-col h-full`}
        style={{ borderColor: `${accent}33` }}
      >
        <FamilyCardGallery family={family} accent={accent} city={city ?? "orlando"} />


        <div className="p-6 flex flex-col flex-1">
          {(() => {
            const match = family.model.match(/^(.+?)\s*\((.+)\)\s*$/);
            const base = match ? match[1] : family.model;
            const parenSub = match ? match[2] : null;
            const roof = family.roofOptions[0];
            const roofLabel = ROOF_LABEL[roof] ?? "";
            const configSub = parenSub ?? (family.roofOptions.length === 1 ? `${roofLabel} Configuration` : null);
            const seatsLabel = minP === maxP ? `${minP} SEATS` : `${minP}-${maxP} SEATS`;

            const specs = family.specsByPassenger;
            const lowSpec = specs?.[minP];
            const cargoText =
              lowSpec?.luggageFootprint ??
              lowSpec?.cargo ??
              family.cargoSpace(minP, roof);
            const seatingText =
              minP === maxP
                ? `${minP} passenger seating`
                : `${minP} & ${maxP} passenger options`;

            return (
              <>

                {/* Title — inline: "Ford Transit 12-15 SEATS" */}
                <div className="mb-2">
                  <p className={`font-display leading-tight ${textClass}`}>
                    <span className="font-extrabold text-2xl md:text-3xl">{base}</span>
                    <br />
                    <span
                      className="font-black italic text-2xl md:text-3xl tracking-tight"
                      style={{ color: accent }}
                    >
                      {seatsLabel.split(" ")[0]}
                    </span>
                    <span
                      className="font-bold uppercase tracking-[0.25em] text-[11px] md:text-xs ml-2"
                      style={{ color: accent, opacity: 0.7 }}
                    >
                      Seats
                    </span>
                  </p>
                  {configSub && (
                    <p
                      className="font-display font-bold italic text-sm md:text-base mt-1"
                      style={{ color: accent }}
                    >
                      ({configSub.replace(/\s*Configuration$/i, "")} Configuration)
                    </p>
                  )}
                </div>

                {/* Mileage rows */}
                {(() => {
                  const r = availability.rate!;
                  const milesPerDay = r.unlimited_miles
                    ? "Unlimited"
                    : (r.miles_included_per_day ?? 250).toLocaleString();
                  const extraMile =
                    r.unlimited_miles
                      ? null
                      : r.extra_mile_rate != null
                        ? `$${r.extra_mile_rate.toFixed(2)}`
                        : null;
                  return (
                    <div className="mb-5 space-y-0.5">
                      <p className={`flex items-baseline gap-2 ${textClass}`}>
                        <span className="font-display font-bold uppercase tracking-[0.18em] text-[11px] md:text-xs opacity-70 w-28 text-right whitespace-nowrap">Miles / Day</span>
                        <span className="font-display font-extrabold text-base md:text-lg">{milesPerDay}</span>
                      </p>
                      {extraMile && (
                        <p className={`flex items-baseline gap-2 ${textClass}`}>
                          <span className="font-display font-bold uppercase tracking-[0.18em] text-[11px] md:text-xs opacity-70 w-28 text-right whitespace-nowrap">Extra Mile</span>
                          <span className="font-display font-extrabold text-base md:text-lg">
                            {extraMile}<span className="text-xs md:text-sm font-bold opacity-70">/mi</span>
                          </span>
                        </p>
                      )}
                    </div>
                  );
                })()}


                {/* Info boxes: Cargo + Seating */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div
                    className="rounded-2xl border-2 px-4 py-3"
                    style={{ borderColor: `${accent}40` }}
                  >
                    <div
                      className="flex items-center gap-1.5 font-display font-bold text-[10px] uppercase tracking-[0.2em] mb-1.5"
                      style={{ color: accent }}
                    >
                      <Luggage size={12} /> Cargo
                    </div>
                    <p className={`text-sm md:text-base font-extrabold leading-tight ${textClass}`}>{family.slug === "chevy-express" ? "See More Info" : family.slug === "mercedes-sprinter-older" ? "15+" : cargoText}</p>
                  </div>
                  <div
                    className="rounded-2xl border-2 px-4 py-3"
                    style={{ borderColor: `${accent}40` }}
                  >
                    <div
                      className="flex items-center gap-1.5 font-display font-bold text-[10px] uppercase tracking-[0.2em] mb-1.5"
                      style={{ color: accent }}
                    >
                      <Users size={12} /> Seating
                    </div>
                    <p className={`text-sm md:text-base font-extrabold leading-tight ${textClass}`}>{seatingText}</p>
                  </div>
                </div>

                {/* CTA buttons */}
                {(() => {
                  const cityParam = city ?? "orlando";
                  const roofParam = family.roofOptions[family.roofOptions.length - 1];
                  const tvParam = family.tvOptions[0];
                  const moreInfoHref = `/configure/${family.slug}?passengers=${minP}&roof=${roofParam}&tv=${tvParam}&city=${cityParam}`;
                  if (onSelectFamily) {
                    return (
                      <div className="mt-auto grid grid-cols-1 gap-2">
                        <button
                          type="button"
                          onClick={() => onSelectFamily(family)}
                          className="inline-flex items-center justify-center gap-1.5 font-display font-bold uppercase tracking-wider text-sm px-4 py-3.5 rounded-full text-white hover:brightness-110 transition"
                          style={{ backgroundColor: accent }}
                        >
                          Select This Van <ChevronRight size={16} />
                        </button>
                        <a
                          href={moreInfoHref}
                          className="text-center font-display font-bold uppercase tracking-wider text-[11px] px-3 py-2 rounded-full hover:underline"
                          style={{ color: accent }}
                        >
                          More Info
                        </a>
                      </div>
                    );
                  }
                  return (
                    <div className="mt-auto grid grid-cols-2 gap-2">
                      <a
                        href={moreInfoHref}
                        className="inline-flex items-center justify-center gap-1.5 font-display font-bold uppercase tracking-wider text-xs px-3 py-3.5 rounded-full border-2 hover:bg-white/40 transition"
                        style={{ borderColor: accent, color: accent }}
                      >
                        More Info
                      </a>
                      <button
                        onClick={() => setOpen(true)}
                        className="inline-flex items-center justify-center gap-1 font-display font-bold uppercase tracking-wider text-xs px-3 py-3.5 rounded-full text-[#0F172A] hover:brightness-110 transition"
                        style={{ backgroundColor: accent }}
                      >
                        Book or Get Quote <ChevronRight size={14} />
                      </button>
                    </div>
                  );
                })()}
              </>
            );
          })()}
        </div>
      </article>

      <ConfigureOptionsSheet family={family} open={open} onClose={() => setOpen(false)} city={city} accent={accent} />
    </>
  );
}

function FamilyCardGallery({ family, accent, city }: { family: VehicleFamily; accent: string; city: string }) {
  const minP = Math.min(...family.passengerOptions);
  const set = family.imagesByPassenger[minP] ?? family.imagesByPassenger[family.passengerOptions[0]];
  const slides: string[] = set
    ? [set.exterior, ...set.interiors].filter(Boolean)
    : [family.coverImage];
  const [idx, setIdx] = useState(0);
  const safeIdx = ((idx % slides.length) + slides.length) % slides.length;
  const go = (delta: number) => setIdx((i) => i + delta);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const trackWidth = useRef<number>(0);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    trackWidth.current = (e.currentTarget as HTMLElement).clientWidth;
    setDragging(true);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current == null || touchStartY.current == null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > Math.abs(dy)) {
      setDragX(dx);
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null || touchStartY.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    const threshold = Math.max(40, trackWidth.current * 0.18);
    if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) {
      go(dx < 0 ? 1 : -1);
    }
    touchStartX.current = null;
    touchStartY.current = null;
    setDragX(0);
    setDragging(false);
  };
  // Two thumbnails:
  //   1) Cargo / rear interior photo (last interior in the curated set)
  //   2) Side-by-side seating diagrams for the smallest and largest passenger
  //      configs (e.g. 12-pass | 15-pass) — same diagrams used on /configure.
  const interiors = set?.interiors ?? [];
  const cargoOverride = getCardCargoOverride(family.slug);
  const cargoSrc = cargoOverride ?? (interiors.length > 0 ? interiors[interiors.length - 1] : null);
  const cargoJump = cargoSrc ? slides.indexOf(cargoSrc) : -1;

  const passOpts = family.passengerOptions;
  const lowPass = passOpts.length > 0 ? Math.min(...passOpts) : 12;
  const highPass = passOpts.length > 1 ? Math.max(...passOpts) : lowPass;
  const showSplitDiagram = highPass !== lowPass;

  const dragPct = trackWidth.current > 0 ? (dragX / trackWidth.current) * 100 : 0;
  const translatePct = -safeIdx * 100 + dragPct;

  return (
    <div className="p-3 pb-0">
      <div
        className={`relative aspect-[5/4] md:aspect-[4/3] overflow-hidden rounded-2xl ${family.coverFit === "contain" ? "bg-gradient-to-br from-neutral-100 to-neutral-200" : "bg-neutral-100"}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="absolute inset-0 flex"
          style={{
            transform: `translate3d(${translatePct}%, 0, 0)`,
            transition: dragging ? "none" : "transform 350ms cubic-bezier(0.22, 1, 0.36, 1)",
            willChange: "transform",
          }}
        >
          {slides.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={(e) => { e.preventDefault(); setLightboxOpen(true); }}
              aria-label="Expand photo"
              tabIndex={i === safeIdx ? 0 : -1}
              className="relative shrink-0 w-full h-full cursor-zoom-in"
            >
              <img
                src={src}
                alt={`${family.model} — photo ${i + 1}`}
                width={1024}
                height={768}
                loading={i === 0 ? "eager" : "lazy"}
                draggable={false}
                className={`absolute inset-0 w-full h-full ${family.coverFit === "contain" ? "object-contain p-4" : "object-cover"} select-none pointer-events-none`}
              />
            </button>
          ))}
        </div>
        <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/85 shadow flex items-center justify-center text-[#1a1a1a] opacity-90 pointer-events-none">
          <Maximize2 size={14} />
        </span>
        {/* Top-right seat count pill */}
        <div className="absolute top-3 right-12 pointer-events-none z-10">
          <span
            className="inline-flex items-center gap-1.5 font-display font-extrabold text-[13px] sm:text-sm text-[#0F172A] px-3 py-1.5 rounded-full shadow-xl ring-2 ring-white/30 backdrop-blur"
            style={{ backgroundColor: `${accent}F2` }}
          >
            <Users size={14} strokeWidth={2.75} />
            {lowPass === highPass ? `${lowPass}` : `${lowPass}-${highPass}`} Pas
          </span>
        </div>
        {/* Bottom-right price badge */}
        <div className="absolute bottom-3 right-3 z-10">
          <StartingPriceBadge family={family} city={city} accent={accent} />
        </div>
        {slides.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous photo"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); go(-1); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center text-[#1a1a1a]"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next photo"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); go(1); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center text-[#1a1a1a]"
            >
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-2 left-3 bg-black/55 text-[#0F172A] text-[10px] font-display font-bold tracking-widest px-2 py-0.5 rounded-full pointer-events-none">
              {safeIdx + 1} / {slides.length}
            </div>
          </>
        )}
      </div>

      {slides.length > 1 && (
        <div className="mt-2 -mx-1 px-1 overflow-x-auto scrollbar-hide">
          <div className="flex gap-1.5 pb-1">
            {slides.map((src, i) => {
              const active = i === safeIdx;
              return (
                <button
                  key={`${src}-${i}`}
                  type="button"
                  aria-label={`Show photo ${i + 1}`}
                  onClick={(e) => { e.preventDefault(); setIdx(i); setLightboxOpen(true); }}
                  className={`relative flex-shrink-0 w-14 h-10 rounded-md overflow-hidden border-2 transition ${active ? "opacity-100" : "opacity-70 hover:opacity-100 border-transparent"}`}
                  style={active ? { borderColor: accent } : undefined}
                >
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </button>
              );
            })}
          </div>
        </div>
      )}
      {lightboxOpen && (
        <Lightbox
          slides={slides}
          startIdx={safeIdx}
          altBase={family.model}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      <div className="grid grid-cols-1 gap-2 mt-2">
        {family.slug === "mercedes-sprinter-executive" ? (
          (() => {
            // Executive: show interior photos instead of seating diagrams.
            const pickA = interiors[0];
            const pickB = interiors[1] ?? interiors[0];
            const jumpA = pickA ? slides.indexOf(pickA) : -1;
            const jumpB = pickB ? slides.indexOf(pickB) : -1;
            return (
              <div className="relative aspect-[16/6] overflow-hidden rounded-xl border-2 bg-white flex" style={{ borderColor: "transparent" }}>
                {pickA && (
                  <button type="button" onClick={(e) => { e.preventDefault(); if (jumpA >= 0) { setIdx(jumpA); setLightboxOpen(true); } }} className="flex-1 relative overflow-hidden cursor-zoom-in">
                    <img src={pickA} alt={`${family.model} interior`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  </button>
                )}
                {pickB && pickB !== pickA && (
                  <>
                    <div className="w-px bg-black/10" />
                    <button type="button" onClick={(e) => { e.preventDefault(); if (jumpB >= 0) { setIdx(jumpB); setLightboxOpen(true); } }} className="flex-1 relative overflow-hidden cursor-zoom-in">
                      <img src={pickB} alt={`${family.model} interior`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    </button>
                  </>
                )}
              </div>
            );
          })()
        ) : (
          <div
            className="relative aspect-[16/5] md:aspect-[16/6] overflow-hidden rounded-xl border-2 bg-white flex"

            style={{ borderColor: "transparent" }}
            aria-label={`${family.model} seating diagrams`}
          >
            <DiagramHalf family={family} passengers={lowPass} />
            {showSplitDiagram && (
              <>
                <div className="w-px bg-black/10" />
                <DiagramHalf family={family} passengers={highPass} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DiagramHalf({ family, passengers }: { family: VehicleFamily; passengers: number }) {
  const roof = family.roofOptions[family.roofOptions.length - 1];
  const diag = getCardDiagramOverride(family.slug, passengers) ?? getDiagram(family.slug, passengers, roof);
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-1 min-w-0">
      <div className="w-full flex-1 flex items-center justify-center min-h-0 overflow-hidden">
        {diag ? (
          <img
            src={diag}
            alt={`${passengers}-passenger ${family.model} diagram`}
            loading="lazy"
            className="max-w-full max-h-full object-contain scale-90 md:scale-[1.05]"
          />
        ) : (
          <div className="w-full pointer-events-none">
            <SeatingDiagram passengers={passengers} hideCaption />
          </div>
        )}
      </div>
      <p className="font-display font-bold text-[11px] uppercase tracking-[0.15em] text-[#1a1a1a]/70 mt-0.5">
        {passengers}-Passenger
      </p>
    </div>
  );
}


function Chip({ accent, children }: { accent: string; children: React.ReactNode }) {
  return (
    <span
      className="font-display font-bold text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full"
      style={{ backgroundColor: `${accent}1f`, color: accent }}
    >
      {children}
    </span>
  );
}

function LiveStartingPrice({
  family,
  city,
  accent,
}: {
  family: VehicleFamily;
  city: string;
  accent: string;
}) {
  // Use smallest passenger count for "starting at" pricing.
  const minP = Math.min(...family.passengerOptions);

  // Track trip dates — pricing is date-adjusted once both are set.
  const [trip, setTrip] = useState<{ pickupDate: string | null; returnDate: string | null }>(() => {
    const t = loadTripDates();
    return { pickupDate: t.pickupDate ?? null, returnDate: t.returnDate ?? null };
  });
  useEffect(() => {
    const sync = () => {
      const t = loadTripDates();
      setTrip({ pickupDate: t.pickupDate ?? null, returnDate: t.returnDate ?? null });
    };
    window.addEventListener("tripdates:change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("tripdates:change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const hasDates = Boolean(trip.pickupDate && trip.returnDate);

  const { rate, isLoading, isUnavailable } = useFamilyPricing(
    family, minP, family.tvOptions[0], city, trip.pickupDate, trip.returnDate,
  );
  // Compute price range from this family's exact CRM vehicle IDs only —
  // do NOT match by make+model (that would pull in older-gen / different
  // roof variants that belong to a separate family card).
  const pricingQ = usePricingPayload(trip.pickupDate, trip.returnDate);
  const familyRates: number[] = [];
  if (pricingQ.data && family.crmVehicleIdByPassenger) {
    const ids = new Set<string>();
    for (const entry of Object.values(family.crmVehicleIdByPassenger)) {
      if (!entry) continue;
      if (typeof entry === "string") ids.add(entry);
      else for (const id of Object.values(entry)) if (id) ids.add(id);
    }
    for (const v of pricingQ.data.vehicles) {
      if (!v.vehicle_type_id || !ids.has(v.vehicle_type_id)) continue;
      const r = resolveRate(v, city);
      if (r?.daily_rate != null) familyRates.push(r.daily_rate);
    }
  }

  const scrollToDates = () => {
    const el = document.getElementById("fleet-filter-panel");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Focus the pickup date input after the scroll begins.
      setTimeout(() => {
        const input = el.querySelector<HTMLInputElement>('input[type="date"]');
        input?.focus();
      }, 350);
    }
  };

  if (isLoading) {
    return (
      <div className="shrink-0 text-right">
        <p className="font-display font-bold text-[10px] uppercase tracking-widest opacity-60">Starting at</p>
        <p className="font-display font-extrabold text-base leading-none opacity-50">…</p>
      </div>
    );
  }
  // No live rate → hide the price chip (parent FleetGrid hides cards via FamilyAvailabilityGate).
  if (isUnavailable || !rate || rate.daily_rate == null) {
    return null;
  }

  const milesText = rate.unlimited_miles
    ? "Unlimited miles"
    : rate.miles_included_per_day != null
      ? `${rate.miles_included_per_day} mi/day included`
      : null;
  const extraMileText =
    !rate.unlimited_miles && rate.extra_mile_rate != null
      ? `$${rate.extra_mile_rate.toFixed(2)}/extra mile`
      : null;

  return (
    <div className="shrink-0 text-right space-y-2">
      <button
        type="button"
        onClick={scrollToDates}
        className="inline-flex items-center gap-2 font-display font-black text-xl md:text-2xl tracking-tight hover:opacity-80 transition"
        style={{ color: accent }}
      >
        <Calendar size={18} />
        {hasDates ? (() => {
          if (familyRates.length === 0) return `$${Math.round(rate.daily_rate)}/day`;
          const lo = Math.round(Math.min(...familyRates));
          const hi = Math.round(Math.max(...familyRates));
          return lo === hi ? `$${lo}/day` : `$${lo}-$${hi}/day`;
        })() : "Select dates for $"}
      </button>

      <div className="font-display font-bold text-[10px] uppercase tracking-widest opacity-70 leading-snug">
        {milesText && <p>{milesText}</p>}
        {extraMileText && <p>{extraMileText}</p>}
        {!hasDates && <p className="opacity-70 mt-1">Pick dates for total $</p>}
      </div>
    </div>
  );

}

function Lightbox({
  slides,
  startIdx,
  altBase,
  onClose,
}: {
  slides: string[];
  startIdx: number;
  altBase: string;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(startIdx);
  const safeIdx = ((idx % slides.length) + slides.length) % slides.length;
  const go = (d: number) => setIdx((i) => i + d);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null || touchStartY.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      go(dx < 0 ? 1 : -1);
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-xl flex flex-col cursor-zoom-out animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
    >
      {/* Top bar */}
      <div className="relative flex items-center justify-between px-4 py-3 z-10" onClick={(e) => e.stopPropagation()}>
        {slides.length > 1 ? (
          <div className="text-[#0F172A]/80 text-xs font-display font-bold tracking-[0.25em] uppercase">
            {safeIdx + 1} <span className="opacity-50">/</span> {slides.length}
          </div>
        ) : <div />}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-[#0F172A]/6 hover:bg-[#0F172A]/10 text-[#0F172A] flex items-center justify-center transition"
        >
          <X size={20} />
        </button>
      </div>

      {/* Image stage */}
      <div
        className="relative flex-1 flex items-center justify-center px-4 sm:px-16 min-h-0"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          key={safeIdx}
          src={slides[safeIdx]}
          alt={`${altBase} — photo ${safeIdx + 1}`}
          className="max-w-full max-h-full object-contain select-none animate-in fade-in duration-150"
          draggable={false} loading="lazy" decoding="async" />
        {slides.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous photo"
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#0F172A]/6 hover:bg-[#0F172A]/12 text-[#0F172A] flex items-center justify-center backdrop-blur transition"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              type="button"
              aria-label="Next photo"
              onClick={(e) => { e.stopPropagation(); go(1); }}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#0F172A]/6 hover:bg-[#0F172A]/12 text-[#0F172A] flex items-center justify-center backdrop-blur transition"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {slides.length > 1 && (
        <div className="px-4 pt-3 pb-4 sm:pb-5" onClick={(e) => e.stopPropagation()}>
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex gap-2 justify-center min-w-max mx-auto">
              {slides.map((src, i) => {
                const active = i === safeIdx;
                return (
                  <button
                    key={`${src}-${i}`}
                    type="button"
                    aria-label={`Show photo ${i + 1}`}
                    onClick={() => setIdx(i)}
                    className={`relative flex-shrink-0 w-16 h-12 sm:w-20 sm:h-14 rounded-md overflow-hidden border-2 transition ${active ? "border-white opacity-100 scale-105" : "border-transparent opacity-50 hover:opacity-90"}`}
                  >
                    <img src={src} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}


function StartingPriceBadge({
  family,
  city,
  accent,
}: {
  family: VehicleFamily;
  city: string;
  accent: string;
}) {
  const minP = Math.min(...family.passengerOptions);
  const [trip, setTrip] = useState<{ pickupDate: string | null; returnDate: string | null }>(() => {
    const t = loadTripDates();
    return { pickupDate: t.pickupDate ?? null, returnDate: t.returnDate ?? null };
  });
  useEffect(() => {
    const sync = () => {
      const t = loadTripDates();
      setTrip({ pickupDate: t.pickupDate ?? null, returnDate: t.returnDate ?? null });
    };
    window.addEventListener("tripdates:change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("tripdates:change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const hasDates = Boolean(trip.pickupDate && trip.returnDate);

  const { rate, isLoading, isUnavailable } = useFamilyPricing(
    family, minP, family.tvOptions[0], city, trip.pickupDate, trip.returnDate,
  );
  const pricingQ = usePricingPayload(trip.pickupDate, trip.returnDate);

  if (isLoading || isUnavailable || !rate || rate.daily_rate == null) return null;

  const familyRates: number[] = [];
  if (pricingQ.data && family.crmVehicleIdByPassenger) {
    const ids = new Set<string>();
    for (const entry of Object.values(family.crmVehicleIdByPassenger)) {
      if (!entry) continue;
      if (typeof entry === "string") ids.add(entry);
      else for (const id of Object.values(entry)) if (id) ids.add(id);
    }
    for (const v of pricingQ.data.vehicles) {
      if (!v.vehicle_type_id || !ids.has(v.vehicle_type_id)) continue;
      const r = resolveRate(v, city);
      if (r?.daily_rate != null) familyRates.push(r.daily_rate);
    }
  }

  let priceText: string;
  if (familyRates.length === 0) {
    priceText = `$${Math.round(rate.daily_rate)}`;
  } else {
    const lo = Math.round(Math.min(...familyRates));
    const hi = Math.round(Math.max(...familyRates));
    priceText = lo === hi ? `$${lo}` : `$${lo}-$${hi}`;
  }

  const scrollToFilter = () => {
    const el = document.getElementById("fleet-filter-panel");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (hasDates) {
    return (
      <span
        className="inline-flex items-baseline gap-1 px-4 py-1.5 rounded-full shadow-lg ring-2 ring-white/30 backdrop-blur text-[#0F172A]"
        style={{ backgroundColor: accent }}
      >
        <span className="font-display font-black italic text-2xl md:text-3xl tracking-tight leading-none">{priceText}</span>
        <span className="font-display font-bold italic text-sm md:text-base leading-none opacity-95">/day</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        scrollToFilter();
      }}
      className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full shadow-lg ring-2 ring-white/30 backdrop-blur text-[#0F172A] cursor-pointer hover:brightness-110 transition"
      style={{ backgroundColor: accent }}
    >
      <Calendar size={14} strokeWidth={2.5} />
      <span className="font-display font-black italic text-xs md:text-sm tracking-tight leading-none">Select Dates For Prices</span>
    </button>
  );
}

